/*
  T6 Help me choose. The rules are handover/QUIZ-LOGIC.md (checked by Cowork, 27 Oct), the same as
  handover/quiz-logic-sim.py. Every fact comes from the product data main-quiz.liquid hands over, so the quiz
  follows Shopify. Answers live in the address: ?q1=leaves&q2=yes&q3=b2&q4=d2 (plus &step=q2 while changing answers).
*/
class HelpMeChoose extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-quiz-data]').textContent);
    // Shopify's t filter escapes apostrophes; decode once so esc() doesn't double them
    const decode = document.createElement('textarea');
    this.t = Object.fromEntries(Object.entries(this.data.t).map(([k, v]) => { decode.innerHTML = v; return [k, decode.value]; }));
    this.R = this.data.robots;
    this.low = parseInt(this.dataset.low, 10) * 100;
    this.high = parseInt(this.dataset.high, 10) * 100;
    this.stretch = parseInt(this.dataset.stretch, 10) * 100;
    this.prepare();

    this.addEventListener('click', (e) => {
      const tile = e.target.closest('[data-answer]');
      if (tile) return this.answer(tile.dataset.answer, tile.value);
      if (e.target.closest('[data-back]')) return this.back();
      if (e.target.closest('[data-change]')) return this.go({ step: 'q1' });
      if (e.target.closest('[data-restart]')) return this.go({}, true);
      return undefined;
    });
    this.addEventListener('submit', (e) => {
      if (e.target.matches('[data-buy]')) this.buy(e);
    });
    window.addEventListener('popstate', () => this.render());
    this.render();
  }

  /* ---------- the facts and the rules (QUIZ-LOGIC.md section 3) ---------- */

  prepare() {
    const R = this.R;
    const better = (x, y) => x.skims >= y.skims && x.clarifies >= y.clarifies && x.ledge <= y.ledge
      && x.pool >= y.pool && x.warranty >= y.warranty;
    // Rule 2: leave out a robot when one at most the stretch dearer is as good or better on every quiz fact
    this.shown = R.filter((r) => !R.some((o) => o !== r && o.price - r.price > 0 && o.price - r.price <= this.stretch && better(o, r)));
    this.minLedge = Math.min(...R.map((r) => r.ledge).filter((l) => l > 0));
    // Rule 3: Q4 bands from the data, each edge a real "cleans platforms from" value
    const edges = [...new Set(this.shown.map((r) => r.ledge).filter((l) => l > this.minLedge))].sort((a, b) => a - b);
    const bottoms = [this.minLedge, ...edges];
    this.depth = {};
    bottoms.forEach((lo, i) => { this.depth[`d${i + 1}`] = { lo, hi: bottoms[i + 1] || null }; });
  }

  bandTop(b) { return b === 'b1' ? this.low : b === 'b2' ? this.high : Infinity; }

  bandBottom(b) { return b === 'b1' ? 0 : b === 'b2' ? this.low : this.high; }

  inBand(r, b) { return b === 'b1' ? r.price <= this.low : b === 'b2' ? r.price > this.low && r.price <= this.high : r.price > this.high; }

  meets(r, q1) {
    if (q1 === 'cloudy') return r.clarifies;
    if (q1 === 'fine-dirt') return !!r.basket;
    return r.skims;
  }

  ledgeOk(r, a) {
    if (a.q2 !== 'yes' || !a.q4 || !this.depth[a.q4]) return true;
    return r.ledge <= this.depth[a.q4].lo;
  }

  pick(a, b = a.q3) {
    const R = this.R;
    const dearest = (rs) => rs.reduce((x, y) => (y.price > x.price ? y : x));
    const cheapest = (rs) => rs.reduce((x, y) => (y.price < x.price ? y : x));
    const band = R.filter((r) => this.inBand(r, b));
    const ok = band.filter((r) => this.meets(r, a.q1) && this.ledgeOk(r, a));
    if (ok.length) return { robot: dearest(ok), why: 'in' };
    if (b !== 'b3') {
      const top = this.bandTop(b);
      const over = R.filter((r) => r.price > top && r.price <= top + this.stretch && this.meets(r, a.q1) && this.ledgeOk(r, a));
      if (over.length) return { robot: cheapest(over), why: 'over' };
    }
    const fit = band.filter((r) => this.ledgeOk(r, a));
    if (fit.length) return { robot: dearest(fit), why: 'in' };
    const below = R.filter((r) => r.price < this.bandBottom(b) && this.ledgeOk(r, a));
    const best = below.filter((r) => this.meets(r, a.q1));
    return { robot: dearest(best.length ? best : below), why: 'under' };
  }

  asksDepth(a) {
    return a.q2 === 'yes' && this.pick({ ...a, q4: null }).robot.ledge > this.minLedge;
  }

  addon(p, q1) {
    if (q1 === 'cloudy' && !p.clarifies) return null;
    if (!p.skims && p.pair) return { kind: 'pair', item: p.pair };
    if (p.clarifies && p.refine) return { kind: 'refine', item: p.refine };
    if (q1 === 'fine-dirt' && p.basket) return { kind: 'basket', item: p.basket };
    return null;
  }

  noMatch(p, a) {
    if (this.meets(p, a.q1)) return '';
    const add = this.addon(p, a.q1);
    if (add && add.kind === 'pair' && a.q1 !== 'cloudy' && a.q1 !== 'fine-dirt') return '';
    const doers = this.shown.filter((r) => this.meets(r, a.q1)).sort((x, y) => x.price - y.price);
    if (!doers.length) return '';
    const top = this.bandTop(a.q3) + (a.q3 === 'b3' ? 0 : this.stretch);
    const blocked = doers.some((r) => r.price <= top && !this.ledgeOk(r, a));
    const kind = a.q1 === 'cloudy' ? 'Cloudy' : a.q1 === 'fine-dirt' ? 'Basket' : 'Skims';
    return this.fill(this.t.noMatch, {
      where: blocked ? this.t.whereLedge : this.t.whereBudget,
      what: this.t[`what${kind}`],
      pick: p.name,
      doer: doers[0].name,
      option: this.t[`option${kind}`],
    });
  }

  alts(p, a) {
    const P = p.price;
    const under = P <= this.bandBottom(a.q3) && a.q3 !== 'b1';
    const over = P > this.bandTop(a.q3);
    let out = [];
    if (over) {
      this.shown.filter((r) => this.inBand(r, a.q3)).sort((x, y) => y.price - x.price).slice(0, 1)
        .forEach((r) => out.push({ label: this.t.spendLess, robot: r }));
    } else if (!under) {
      this.shown.filter((r) => r.price < P).sort((x, y) => y.price - x.price).slice(0, 2)
        .forEach((r) => out.push({ label: this.t.spendLess, robot: r }));
    }
    if (under) {
      this.shown.filter((r) => this.inBand(r, a.q3)).sort((x, y) => y.price - x.price)
        .forEach((r) => out.push({ label: this.t.skipLedge, robot: r }));
    } else if (!over) {
      const dearer = this.shown.filter((r) => r.price > P).sort((x, y) => x.price - y.price);
      const go = dearer.filter((r) => this.meets(r, a.q1)).concat(dearer.filter((r) => r.skims));
      if (go.length) out.push({ label: this.t.goFurther, robot: go[0] });
    }
    out = out.length > 2 && !under ? out.slice(-2) : out.slice(0, 2);
    const floor = a.q4 && this.depth[a.q4] ? this.depth[a.q4].lo : this.minLedge;
    return out.map((x) => ({
      ...x,
      note: a.q2 === 'yes' && x.robot.ledge > floor ? this.fill(this.t.needsDepth, { cm: x.robot.ledge }) : '',
    }));
  }

  /* ---------- address and steps ---------- */

  answers() {
    const q = new URLSearchParams(window.location.search);
    const a = { q1: q.get('q1'), q2: q.get('q2'), q3: q.get('q3'), q4: q.get('q4'), step: q.get('step') };
    if (!this.querySelector(`[data-answer="q1"][value="${CSS.escape(a.q1 || '')}"]`)) a.q1 = null;
    if (!['yes', 'no'].includes(a.q2)) a.q2 = null;
    if (!['b1', 'b2', 'b3'].includes(a.q3)) a.q3 = null;
    if (!this.depth[a.q4]) a.q4 = null;
    return a;
  }

  stepFor(a) {
    if (a.step && ['q1', 'q2', 'q3', 'q4'].includes(a.step)) return a.step;
    if (!a.q1) return 'q1';
    if (!a.q2) return 'q2';
    if (!a.q3) return 'q3';
    if (this.asksDepth(a) && !a.q4) return 'q4';
    return 'result';
  }

  go(changes, clear = false) {
    const url = new URL(window.location.href);
    if (clear) ['q1', 'q2', 'q3', 'q4', 'step'].forEach((k) => url.searchParams.delete(k));
    Object.entries(changes).forEach(([k, v]) => (v ? url.searchParams.set(k, v) : url.searchParams.delete(k)));
    window.history.pushState(null, '', url);
    this.render();
    this.querySelector('.hmc-quiz').scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  answer(q, value) {
    const a = { ...this.answers(), [q]: value, step: null };
    const next = { q1: 'q2', q2: 'q3', q3: 'q4', q4: null }[q];
    const changes = { [q]: value, step: null };
    if (q !== 'q4') changes.q4 = null;
    // While changing answers, keep walking through the questions
    if (this.answers().step && next && (next !== 'q4' || this.asksDepth(a))) changes.step = next;
    this.go(changes);
  }

  back() {
    const a = this.answers();
    const step = this.stepFor(a);
    const prev = { q2: 'q1', q3: 'q2', q4: 'q3', result: this.asksDepth(a) ? 'q4' : 'q3' }[step] || 'q1';
    this.go({ step: prev });
  }

  /* ---------- screens ---------- */

  render() {
    const a = this.answers();
    if (a.q4 && !this.asksDepth(a)) a.q4 = null;
    const step = this.stepFor(a);
    this.querySelectorAll('[data-step]').forEach((el) => { el.hidden = el.dataset.step !== step; });
    // Question 1 (also when you come Back to it) has the dark header; every later screen has none
    this.classList.toggle('is-going', step !== 'q1');
    this.classList.toggle('is-result', step === 'result');
    this.querySelectorAll('[data-landing]').forEach((el) => { el.hidden = step !== 'q1'; });
    this.querySelectorAll('[data-answer]').forEach((tile) => tile.classList.toggle('is-on', a[tile.dataset.answer] === tile.value));
    if (step === 'q4') this.renderDepth(a);
    if (step === 'result') this.renderResult(a);
    this.renderSoFar(a, step);
  }

  label(q, v) {
    const tile = this.querySelector(`[data-answer="${q}"][value="${CSS.escape(v || '')}"] [data-label]`);
    return tile ? tile.textContent.trim() : '';
  }

  renderSoFar(a, step) {
    const box = this.querySelector('[data-so-far]');
    const parts = [];
    if (a.q1 && step !== 'q1') parts.push(this.label('q1', a.q1));
    if (a.q2 && ['q3', 'q4'].includes(step)) parts.push(`${this.t.ledge}: ${a.q2 === 'yes' ? this.t.yes.toLowerCase() : this.t.no.toLowerCase()}`);
    if (a.q3 && step === 'q4') parts.push(this.label('q3', a.q3));
    box.hidden = !parts.length || step === 'result';
    box.innerHTML = `${this.esc(this.t.soFar)} ${parts.map((p) => `<span class="hmc-tag">${this.esc(p)}</span>`).join('')}`;
  }

  renderDepth(a) {
    const wrap = this.querySelector('[data-q4-tiles]');
    const keys = Object.keys(this.depth);
    wrap.innerHTML = keys.map((key, i) => {
      const text = this.depthText(key);
      // Board T6 Depth: a side-on drawing of the ledge, the water over it deeper on each tile
      const top = keys.length > 1 ? Math.round(28 + (36 * i) / (keys.length - 1)) : 42;
      const art = `<svg class="hmc-depth-art" viewBox="0 0 260 120" aria-hidden="true"><rect width="260" height="120" fill="#CFE6F2"/><rect y="${top}" width="150" height="${120 - top}" fill="#B9BDC1"/><rect x="150" y="112" width="110" height="8" fill="#B9BDC1"/><line x1="120" y1="3" x2="120" y2="${top - 3}" stroke="#1A1A1A" stroke-width="2"/><path d="M114 9 120 3 126 9M114 ${top - 9} 120 ${top - 3} 126 ${top - 9}" fill="none" stroke="#1A1A1A" stroke-width="2"/></svg>`;
      return `<button type="button" class="hmc-tile hmc-tile--depth${a.q4 === key ? ' is-on' : ''}" data-answer="q4" value="${key}">${art}<b data-label class="num">${this.esc(text)}</b></button>`;
    }).join('');
    const p = this.pick({ ...a, q4: null }).robot;
    const lowest = this.shown.filter((r) => r.ledge === this.minLedge).map((r) => r.name);
    this.querySelector('[data-q4-why]').textContent = this.fill(this.t.depthWhy, { pick: p.name, cm: p.ledge, robots: this.list(lowest), min: this.minLedge });
  }

  depthText(key) {
    const keys = Object.keys(this.depth);
    const band = this.depth[key];
    if (!band) return '';
    if (key === keys[0]) return this.fill(this.t.depthBandUnder, { cm: band.hi });
    if (key === keys[keys.length - 1]) return this.fill(this.t.depthBandOver, { cm: band.lo });
    return this.fill(this.t.depthBandBetween, { low: band.lo, high: band.hi });
  }

  renderResult(a) {
    const { robot: p, why } = this.pick(a);
    const t = this.t;
    const band = this.label('q3', a.q3);
    const bandLower = band.charAt(0).toLowerCase() + band.slice(1);
    const lines = [];
    if (this.meets(p, a.q1)) lines.push(a.q1 === 'cloudy' ? t.whyClarifies : a.q1 === 'fine-dirt' ? t.whyBasket : t.whySkims);
    if (a.q2 === 'yes') lines.push(this.fill(t.whyLedge, { cm: p.ledge }));
    if (why === 'over') lines.push(this.fill(t.whyOverBudget, { price: p.priceText, over: this.money(p.price - this.bandTop(a.q3)), band: bandLower }));
    else if (why === 'under') {
      // Rule 4d: name the robots in the budget and the least water any of them needs
      const inBudget = this.R.filter((r) => this.inBand(r, a.q3)).sort((x, y) => x.price - y.price);
      const cm = Math.min(...inBudget.map((r) => r.ledge));
      lines.push(this.fill(inBudget.length > 1 ? t.whyUnderBudget : t.whyUnderBudgetOne, { price: p.priceText, robots: this.list(inBudget.map((r) => r.name)), cm }));
    }
    else lines.push(this.fill(t.whyInBudget, { price: p.priceText, band: bandLower }));
    if (lines.length < 3) lines.push(this.fill(t.whyPool, { size: p.pool }));
    if (lines.length < 3) lines.push(this.fill(t.whyWarranty, { years: p.warranty }));

    const add = this.addon(p, a.q1);
    const honest = this.noMatch(p, a);
    const alts = this.alts(p, a);
    const bigger = this.shown.filter((r) => r.pool > p.pool);
    const maxPool = Math.max(...this.shown.map((r) => r.pool));
    const answersList = [
      [t.inPool, this.label('q1', a.q1)],
      [t.ledge, a.q2 === 'yes' ? t.yes : t.no],
      [t.budget, band],
    ];
    if (a.q4) answersList.push([t.depth, this.depthText(a.q4)]);
    const compareUrl = `${this.data.compare}?robots=${[p, ...alts.map((x) => x.robot)].map((r) => r.handle).join(',')}`;
    const check = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

    let addHtml = '';
    if (add && add.item.available !== false) {
      const total = this.money(p.price + add.item.price);
      // A robot that already has a clarifying kit in the box gets the kit as spares
      const spare = add.kind === 'refine' && p.hasKit;
      const note = add.kind === 'basket' ? this.data.text.basketNote : spare ? this.fill(t.spareKitLine, { robot: p.name }) : add.item.purpose;
      addHtml = `<label class="hmc-addon"><input type="checkbox" data-addon value="${add.item.variant}">
        ${add.item.img ? `<img src="${add.item.img}" alt="" width="56" height="56" loading="lazy">` : '<span></span>'}
        <span><b>${this.esc(spare ? t.spareKit : t.completeSetup)}</b>${this.esc(this.fill(t.addAddon, { name: add.item.name, price: add.item.priceText }))}${add.item.mode ? ` · ${this.esc(add.item.mode)}` : ''}
        ${note ? `<small>${this.esc(note)}</small>` : ''}<small>${this.esc(this.fill(t.together, { total }))}</small></span></label>`;
    }

    const alt = (x) => {
      const r = x.robot;
      const ticks = [
        [t.tickClean, true],
        [t.tickSkims, r.skims],
        [t.tickClarifies, r.clarifies],
        [this.fill(t.tickLedge, { cm: r.ledge }), true],
        [t.tickAi, r.ai],
        [this.fill(t.tickPool, { size: r.pool }), true],
      ];
      let pairNote = '';
      if (!r.skims && r.pair && (a.q1 === 'leaves' || a.q1 === 'everything')) {
        pairNote = this.fill(t.pairNote, { article: this.article(r.pair.name), name: r.pair.name, price: r.pair.priceText, total: this.money(r.price + r.pair.price) });
      }
      return `<article class="hmc-alt">
        <div class="hmc-alt__top">
          <a class="hmc-alt__media" href="${r.url}" tabindex="-1" aria-hidden="true">${r.img ? `<img src="${r.img}" alt="" width="800" height="600" loading="lazy">` : ''}</a>
          <span class="hmc-alt__id"><span class="hmc-alt__label">${this.esc(x.label)}</span><b class="hmc-alt__name">${this.esc(r.name)}</b><span class="hmc-alt__price"><b class="num">${this.esc(r.priceText)}</b> · ${this.esc(this.years(r.warranty))}</span></span>
        </div>
        ${r.purpose ? `<p class="hmc-alt__purpose">${this.esc(r.purpose)}</p>` : ''}
        <ul class="hmc-ticks">${ticks.map(([txt, yes]) => `<li class="${yes ? '' : 'is-no'}">${yes ? check : '<span class="hmc-ticks__no" aria-hidden="true"></span><span class="visually-hidden">${this.esc(t.no)}: </span>'}<span>${this.esc(txt)}</span></li>`).join('')}</ul>
        ${pairNote ? `<p class="hmc-note">${this.esc(pairNote)}</p>` : ''}
        ${x.note ? `<p class="hmc-note">${this.esc(x.note)}</p>` : ''}
        <span class="hmc-alt__fill"></span>
        <span class="hmc-actions hmc-actions--pair">
          <a class="btn btn--outline" href="${r.url}">${this.esc(t.viewDetails)}</a>
          ${this.buyForm(r)}
        </span>
      </article>`;
    };

    this.querySelector('[data-step="result"]').innerHTML = `
      <div class="hmc-result__head">
        <span class="hmc-result__eyebrow">${check}${this.esc(t.complete)}</span>
        <h1 class="hmc-result__title">${this.esc(this.data.text.resultHeading)}</h1>
        <p class="hmc-result__lede">${this.esc(this.data.text.resultLede)}</p>
      </div>
      <div class="hmc-answers">
        <div class="hmc-answers__list"><b>${this.esc(t.yourAnswers)}</b>
        <dl>${answersList.map(([k, v]) => `<div><dt>${this.esc(k)}</dt><dd>${this.esc(v)}</dd></div>`).join('')}</dl></div>
        <span class="hmc-answers__do"><button type="button" class="btn btn--outline btn--sm" data-change>${this.esc(t.change)}</button>
        <button type="button" class="hmc-answers__restart" data-restart>${this.esc(t.restart)}</button></span>
      </div>
      <div class="hmc-pick">
        <div class="hmc-pick__media">${p.badge ? `<span class="hmc-pick__badge">${this.esc(p.badge)}</span>` : ''}${p.img ? `<img src="${p.img}" alt="${this.esc(p.name)}" width="800" height="600">` : ''}</div>
        <div class="hmc-pick__body">
          <span class="hmc-pick__eyebrow">${this.esc(t.bestMatch)}</span>
          <h2 class="hmc-pick__name">${this.esc(p.name)}</h2>
          <b>${this.esc(t.why)}</b>
          <ul class="hmc-pick__why">${lines.map((l) => `<li>${check}<span>${this.esc(l)}</span></li>`).join('')}</ul>
          ${honest ? `<p class="hmc-note" data-honest>${this.esc(honest)}</p>` : ''}
          <span class="hmc-pick__money"><span class="hmc-pick__price"><b class="num">${this.esc(p.priceText)}</b> · ${this.esc(this.years(p.warranty))}</span>
          <span class="hmc-pick__later">${this.esc(p.payLater)}</span></span>
          <form data-buy data-variant="${p.variant}">
            ${addHtml}
            <span class="hmc-actions hmc-actions--pair">
              ${p.available ? `<button type="submit" class="btn btn--buy btn--lg">${this.esc(p.mode === 'Pre-order' ? t.preOrder : t.addToCart)}</button>` : `<span class="btn btn--outline btn--lg is-disabled" aria-disabled="true">${this.esc(t.soldOut)}</span>`}
              <a class="btn btn--outline btn--lg" href="${p.url}">${this.esc(t.viewDetails)}</a>
            </span>
          </form>
        </div>
      </div>
      ${alts.length ? `<div class="hmc-alts__head"><h2>${this.esc(t.otherFits)}</h2><a class="btn btn--outline" href="${compareUrl}">${this.esc(t.compareThese)}</a></div>
      <div class="hmc-alts">${alts.map(alt).join('')}</div>` : ''}
      ${bigger.length ? `<p class="hmc-pool">${this.esc(this.fill(t.poolLine, { size: p.pool, robots: this.list(bigger.map((r) => r.name)), verb: bigger.length > 1 ? 'are' : 'is', max: maxPool, phone: this.data.phone })).replace(this.esc(this.data.phone), `<a href="tel:${this.data.phone.replace(/\s/g, '')}">${this.esc(this.data.phone)}</a>`)}</p>` : ''}`;
  }

  buyForm(r) {
    if (!r.available) return `<span class="btn btn--outline is-disabled" aria-disabled="true">${this.esc(this.t.soldOut)}</span>`;
    return `<form method="post" action="${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart/add"><input type="hidden" name="id" value="${r.variant}"><button type="submit" class="btn btn--buy">${this.esc(r.mode === 'Pre-order' ? this.t.preOrder : this.t.addToCart)}</button></form>`;
  }

  async buy(e) {
    e.preventDefault();
    const form = e.target;
    const items = [{ id: Number(form.dataset.variant), quantity: 1 }];
    const extra = form.querySelector('[data-addon]:checked');
    if (extra) items.push({ id: Number(extra.value), quantity: 1 });
    const button = form.querySelector('[type="submit"]');
    // The cart panel (assets/cart.js) adds and opens with "Added to your cart"
    if (window.BBCart) {
      window.BBCart.add(items, button);
      return;
    }
    button.disabled = true;
    button.textContent = this.t.adding;
    try {
      const res = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(String(res.status));
      window.location.href = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart`;
    } catch (err) {
      button.disabled = false;
      button.textContent = this.t.addError;
    }
  }

  /* ---------- helpers ---------- */

  article(name) { return /^[aeiou]/i.test(String(name).trim()) ? this.t.articleAn : this.t.articleA; }

  years(n) { return this.t.warranty.replace('9', n); }

  money(cents) {
    const dollars = cents / 100;
    return `$${dollars.toLocaleString('en-AU', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
  }

  list(items) {
    if (items.length < 2) return items.join('');
    return `${items.slice(0, -1).join(', ')} ${this.t.and} ${items[items.length - 1]}`;
  }

  fill(text, values) {
    return Object.entries(values).reduce((out, [k, v]) => out.split(`[${k}]`).join(String(v)), text || '');
  }

  esc(text) {
    const div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
  }
}

if (!customElements.get('help-me-choose')) customElements.define('help-me-choose', HelpMeChoose);
